import { palette } from '@/theme/constants';
import { Box, CircularProgress, Fab, Fade, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';

import {
  clearCollaborators,
  clearExtraContent,
  clearTemplatesData,
  CloseTypeStoryModal,
  getProfileStories,
  OpenTypeStoryModal,
  showActualSection,
  openModal,
  openSubscriptionPopup,
} from '@/store/actions';
import { authSelector, homeSelector, intermitenceSelector, pendingStorySelector } from '@/store/selectors';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UseFirstRender, UseIntermitence, UseScrollMargin } from '@/hooks';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import CreateStoryModal from '@/components/CreateStoryModal';
import { TypeStory } from '../CreateStory/components';
import BlankScreenContainer from './elements/BlankComponent';
import { checkStoryCreationLimit } from '@/utils';
interface Story {
  user_id: number;
  title: string;
  fileSize: number;
  description: string;
  isPrivate: boolean;
  password: string;
  cover_image: string;
  story_details: object;
  status: string;
}
interface prop {
  flag?: boolean;
}
const StoryItem = dynamic(() => import('./elements/StoryItem'), {
  loading: () => <CircularProgress sx={{ color: palette.primary }} />,
  ssr: false,
});

const CategoriesContainer = dynamic(() => import('./elements/CategoriesContainer'), {
  loading: () => <CircularProgress sx={{ color: palette.primary }} />,
  ssr: false,
});

export const Home = ({ flag }: prop) => {
  const { t } = useTranslation();

  const { separation, typestorymodal } = useSelector(intermitenceSelector);
  const [Loading, setLoading] = useState(true);
  const { criterias, stories, storiesResult, homeLoading } = useSelector(homeSelector);
  const pendingStory = useSelector(pendingStorySelector);
  const [PendingStory, SetPendingStory] = useState<Story | null>(null);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const containerRef1 = useRef(null);
  const containerRef2 = useRef(null);
  const containerRef3 = useRef(null);
  const [isOpen, setIsOpen] = useState(flag ? flag : false);
  const containerMargin1 = UseScrollMargin(containerRef1);
  const containerMargin2 = UseScrollMargin(containerRef2);
  const containerMargin3 = UseScrollMargin(containerRef3);
  const { user } = useSelector(authSelector);

  const { status: privateStatus, switchStatus: switchPublication } = UseIntermitence();
  const router = useRouter();
  const [showLoading, setShowLoading] = useState(true);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  
  // Extract handoff query param (only present on first load after login)
  const handoff =
    router.isReady && typeof router.query.handoff === 'string'
      ? router.query.handoff
      : undefined;
  const useBodyBackground = (color: string = '#fff') => {
    useEffect(() => {
      document.body.style.background = color;

      return () => {
        document.body.style.background = '';
      };
    }, [color]);
  };
  useBodyBackground('#F1F7FA');

  UseFirstRender(() => {
    dispatch(getProfileStories());

    let pendingStory = null;

    if (typeof window !== 'undefined') {
      const pstory = localStorage.getItem('pendingStory');
      pendingStory = pstory ? JSON.parse(pstory) : null;
    }
    SetPendingStory(pendingStory);
    setLoading(false);
  }, []);

  const handleItemClick = (item: any) => {
    if (!item.id) {
      router.push(`/app/story/${item?.title}/update`);
    } else if (item.resultContain && item.private) {
      const validRoles = ['Story_Collaborator', 'Story_Viewer', 'Story_Admin'];

      const hasRole = user?.roles?.find(
        (role: any) => role.story_id === item?.id && validRoles.includes(role.role.name),
      );

      const userIsCreator = user?.id === item?.user_id;

      if (hasRole || userIsCreator) {
        router.push(`/app/story/${item?.url}`);
      } else {
        dispatch(showActualSection(item?.title));
        switchPublication();
      }
    } else {
      dispatch(showActualSection(item?.title));
      router.push(`/app/story/${item?.url}`);
    }
  };

  useEffect(() => {
    dispatch(clearExtraContent());
    dispatch(clearTemplatesData());
    dispatch(clearCollaborators());
  }, []);

  const handlePublication = () => {
    switchPublication();
  };

  const handleCreateMemvy = async () => {
    // Check story creation limit before opening modal
    const storyLimitCheck = await checkStoryCreationLimit(user.id, user.token);
    if (!storyLimitCheck.canCreate) {
      // Show error message
      if (storyLimitCheck.reason === 'single_story_limit_reached') {
        dispatch(openModal({
          content: 'Your purchase is for a single Story hosted for 5 years. If you would like to subscribe to be able to create more stories, please contact support@memvy.com.',
          hasLink: true,
          linkText: 'support@memvy.com',
          linkPath: 'mailto:support@memvy.com'
        }));
      } else if (storyLimitCheck.reason === 'free_story_limit_reached') {
        // Open subscription popup for users who have used their free story
        localStorage.setItem('router_history', '/app/home');
        dispatch(openSubscriptionPopup());
      } else {
        dispatch(openModal({
          content: storyLimitCheck.reason || 'Unable to create story at this time.'
        }));
      }
      return;
    }
    // If check passes, open the modal
    dispatch(OpenTypeStoryModal());
  };
  const handleOpenModal = async () => {
    const storyLimitCheck = await checkStoryCreationLimit(user.id, user.token);
    if (!storyLimitCheck.canCreate) {
      if (storyLimitCheck.reason === 'single_story_limit_reached') {
        dispatch(openModal({
          content: 'Your purchase is for a single Story hosted for 5 years. If you would like to subscribe to be able to create more stories, please contact support@memvy.com.',
          hasLink: true,
          linkText: 'support@memvy.com',
          linkPath: 'mailto:support@memvy.com'
        }));
      } else if (storyLimitCheck.reason === 'free_story_limit_reached') {
        localStorage.setItem('router_history', '/app/home');
        dispatch(openSubscriptionPopup());
      } else {
        dispatch(openModal({
          content: storyLimitCheck.reason || 'Unable to create story at this time.'
        }));
      }
      return;
    }
    setIsOpen(true);
    dispatch(OpenTypeStoryModal());
  };
  const handleCloseModal = () => {
    dispatch(CloseTypeStoryModal());
  };

  const isLoaded = !homeLoading && !Loading;

  // Ensure loading screen shows for at least 1 second
  useEffect(() => {
    setMinTimeElapsed(false);
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isLoaded && minTimeElapsed) {
      // Wait for fade transition to complete before hiding
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 750);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(true);
    }
  }, [isLoaded, minTimeElapsed]);

  return (
    <>
      <>
        {showLoading && (
          <Fade in={!isLoaded || !minTimeElapsed} timeout={750}>
            <Box
              width={'100%'}
              height={'100vh'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 9999,
              }}>
              <Image priority src='/images/Loading.svg' quality={'100'} alt='loading' fill style={{ objectFit: 'cover' }} />
            </Box>
          </Fade>
        )}
        {isLoaded && minTimeElapsed && !typestorymodal && (
          <Box
            display={'flex'}
            width={'100%'}
            height={isMobile ? 'auto' : '100vh'}
            minHeight={isMobile ? '100vh' : 'auto'}
            paddingTop={isMobile ? '0' : separation}
            paddingBottom={isMobile ? '6rem' : '0'}
            position={'relative'}
            flexDirection={'column'}
            zIndex={1}
            overflow={isMobile ? 'visible' : 'auto'}>
            {stories?.draftStories?.length == 0 &&
            stories?.publishedStories?.length == 0 &&
            stories?.collaboratorStories?.length == 0 &&
            stories?.purchasedStories?.length == 0 &&
            criterias?.search?.length == 0 &&
            PendingStory === null &&
            !homeLoading ? (
              <Box
                width={isMobile ? '100%' : '1200px'}
                maxWidth={'100%'}
                margin={'0 auto'}
                px={isMobile ? '0rem' : '1rem'}
                display={'flex'}
                position={isMobile ? 'fixed' : 'relative'}
                flexDirection={'column'}
                zIndex={1}>
                {!isMobile && (
                  <Box
                    ml={!isMobile ? '0rem' : '1.25rem'}
                    mt={'50px'}
                    display={'flex'}
                    alignItems={'center'}
                    alignContent={'center'}
                    gap={'0.5rem'}>
                    <Typography
                      color={palette?.background}
                      my={!isMobile ? '0 1.75rem' : '0 1.25rem'}
                      variant={isMobile ? 'h5' : 'h4'}>
                      {'My Memvys'}
                    </Typography>
                  </Box>
                )}
                <BlankScreenContainer onclick={handleOpenModal} isMobile={isMobile} />
              </Box>
            ) : (
              <Box
                width={isMobile ? '100%' : '1200px'}
                maxWidth={'100%'}
                margin={'0 auto'}
                px={isMobile ? '0rem' : '1rem'}
                display={'flex'}
                position={'relative'}
                mb={'1.2rem'}
                mt={isMobile ? '1.25rem' : ''}
                flexDirection={'column'}
                zIndex={1}>
                {criterias?.search?.length == 0 &&
                criterias?.prompts?.length == 0 &&
                criterias?.collaborators?.length == 0 ? (
                  <>
                    {!isMobile && (
                      <Box
                        ml={!isMobile ? '1rem' : '1.25rem'}
                        mt={'50px'}
                        mb={'2rem'}
                        display={'flex'}
                        alignItems={'center'}
                        alignContent={'center'}
                        gap={'0.5rem'}>
                        <Typography
                          color={palette?.background}
                          my={!isMobile ? '0 1.75rem' : '0 1.25rem'}
                          variant={isMobile ? 'h5' : 'h4'}>
                          {'My Memvys'}
                        </Typography>
                      </Box>
                    )}
                    {PendingStory && pendingStory?.user_id == user?.id && (
                      <CategoriesContainer
                        {...{
                          title: 'Pending',
                          icon: '/icons/draft.svg',
                          data: PendingStory,
                          t,
                          isMobile,
                          handleItemClick,
                          scrollMargin: containerMargin2,
                          containerRef: containerRef2,
                        }}
                      />
                    )}
                    {(!router.query.p || router.query.p === 'published') && stories?.publishedStories?.length > 0 && (
                      <CategoriesContainer
                        {...{
                          title: 'published',
                          icon: '/icons/published.svg',
                          data: stories.publishedStories,
                          t,
                          isMobile,
                          handleItemClick,
                          scrollMargin: containerMargin2,
                          containerRef: containerRef2,
                        }}
                      />
                    )}
                    {(!router.query.p || router.query.p === 'purchased') && stories?.purchasedStories?.length > 0 && (
                      <CategoriesContainer
                        {...{
                          title: 'purchased',
                          data: stories.purchasedStories,
                          t,
                          isMobile,
                          handleItemClick,
                          scrollMargin: containerMargin2,
                          containerRef: containerRef2,
                        }}
                      />
                    )}
                    {(!router.query.p || router.query.p === 'drafts') && stories?.draftStories?.length > 0 && (
                      <CategoriesContainer
                        {...{
                          title: 'drafts',
                          data: stories.draftStories,
                          icon: '/icons/drafts.svg',
                          t,
                          isMobile,
                          handleItemClick,
                          scrollMargin: containerMargin1,
                          containerRef: containerRef1,
                        }}
                      />
                    )}

                    {(!router.query.p || router.query.p === 'collaborators') &&
                      stories?.collaboratorStories?.length > 0 && (
                        <CategoriesContainer
                          {...{
                            title: 'Collaborating',
                            icon: '/icons/collab.svg',
                            data: stories.collaboratorStories,
                            t,
                            isMobile,
                            handleItemClick,
                            scrollMargin: containerMargin3,
                            containerRef: containerRef3,
                          }}
                        />
                      )}
                  </>
                ) : (
                  <>{resultsContainer(storiesResult, isMobile, handleItemClick, t, privateStatus, handlePublication)}</>
                )}
              </Box>
            )}
          </Box>
        )}
        {isLoaded &&
          minTimeElapsed &&
          stories?.draftStories?.length == 0 &&
          stories?.publishedStories?.length == 0 &&
          stories?.collaboratorStories?.length == 0 &&
          stories?.purchasedStories?.length == 0 &&
          criterias?.search?.length == 0 &&
          PendingStory === null &&
          !homeLoading && (
            <>
              <Box
                sx={{
                  position: 'fixed',
                  right: { xs: '1.75rem', md: '1.75rem', lg: '8.5rem', xl: '24.5rem' },
                  bottom: { xs: '13rem', lg: '12.3rem' },
                  width: '189px',
                  height: '68px',
                }}>
                <Image priority src={'/images/start-creating.svg'} width={189} height={68} alt='down' />
              </Box>
              <Box
                sx={{
                  position: 'fixed',
                  right: { xs: '5.6rem', md: '6.75rem', lg: '10.5rem', xl: '28.5rem' },
                  bottom: { xs: '7.8rem', md: '5rem', lg: '7.6rem' },
                }}>
                <Image priority src={'/images/arrow-down.svg'} width={29} height={71} alt='down' />
              </Box>
            </>
          )}
        {isLoaded && minTimeElapsed && (
          <Fab
            size={isMobile ? 'medium' : 'large'}
            onClick={handleCreateMemvy}
            sx={{
              width: '12rem',
              height: '4rem',
              display: 'flex',
              backgroundColor: palette?.white,
              gap: '1rem',
              borderRadius: '100px',
              position: 'fixed',
              right: { xs: '0.75rem', md: '2.5rem', lg: '2.5rem', xl: '22.5rem' },
              bottom: { xs: '3rem', lg: '3.5rem' },
              boxShadow: '0px 8px 30px 0px rgba(33, 45, 109, 0.12)',
            }}>
            <Image priority
              src={`/icons/add.svg`}
              width={isMobile ? 20 : 26}
              height={isMobile ? 20 : 26}
              quality={80}
              alt={t('add')}
            />
            <Typography color={palette?.background}>Create Memvy</Typography>
          </Fab>
        )}
        <CreateStoryModal />
      </>
      {/* )} */}
      {typestorymodal && <TypeStory handleBack={handleCloseModal} />}
    </>
  );
};

const resultsContainer = (
  data: any,
  isMobile: boolean,
  handleItemClick: any,
  t: any,
  privateStatus: boolean,
  handlePublication?: any,
) => {
  return (
    <Grid
      container
      paddingRight={'1rem'}
      paddingLeft={'0.5rem'}
      columnSpacing={isMobile ? 1 : 1.5}
      width={isMobile ? '100%' : '100%'}
      margin={'2rem auto'}>
      {data?.length > 0 ? (
        <>
          {(data ?? [])?.map((item: any) => {
            return (
              <StoryItem
                key={item.id}
                isMobile={isMobile}
                item={{ ...item, resultContain: true }}
                isResult
                handleItemClick={handleItemClick}
                privateStatus={privateStatus}
                handlePublication={handlePublication}
              />
            );
          })}
        </>
      ) : (
        <Box
          display={'flex'}
          height={'calc(100vh - 12.375rem)'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          width={'100%'}>
          <Image priority src={`/icons/notfound.svg`} alt={'logo'} width={90} height={90} quality={80} />
          <Typography color={palette?.background} fontSize={'2rem'}>
            {t('not_results')}
          </Typography>
          <Typography color={palette?.background} variant={'h4'}>
            {t('broadening_search')}
          </Typography>
        </Box>
      )}
    </Grid>
  );
};
